import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  TextField,
  Typography,
  MenuItem,
  Select,
  InputLabel,
  Radio,
  RadioGroup,
  FormControlLabel,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Checkbox,
  Autocomplete,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MapIcon from '@mui/icons-material/Map';
import RouteIcon from '@mui/icons-material/Route';
import {
  CHECKLIST,
  excludeFields,
  FINALIZE,
  GENERAL,
  HAZARDS,
  MACHINES,
  MAP,
  OWNERS,
  pickFields,
  SITE_HARVESTING,
  SITE_INFO,
  SITE_VOLUME,
  SUB_COMPARTMENTS,
} from './Data/NewSiteData';
import useNewSiteAssessment from './CustomHooks/useNewSiteAssessment';
import MapEditor from './components/MapEditor';
import MapRouteView from './components/MapRoutView';
import SubCompartmentSection from './components/SubCompartmentComponents/SubCompartmentSection';
import AutoSaveIndicator from './components/AutoSaveIndicator';
import AddOwnerDrawer from './components/AddOwnerDrawer';

// SIMPLE FLAT CONFIGURATION
const assessmentConfig = {
  'Purchase & Sales': {
    categories: ['Standing / Windblow Timber', 'Forest Floor', 'Roadside', 'Sawmill Residue', 'Delivered To Our Yard'],
    fields: {
      'Standing / Windblow Timber': [
        ...GENERAL,

        ...MAP,
        ...SUB_COMPARTMENTS,
        ...SITE_INFO,
        ...HAZARDS,
        ...SITE_VOLUME,
        ...SITE_HARVESTING,
        ...MACHINES,
        ...CHECKLIST,
        ...FINALIZE,
      ],
      'Forest Floor': [
        ...GENERAL,

        ...MAP,
        ...excludeFields(SITE_INFO, ['urea', 'remainingTreesPerHa']),
        ...HAZARDS,
        ...excludeFields(SITE_VOLUME, ['averageTree', 'treesPerHectare', 'percentOfForestCovered']),
        ...excludeFields(SITE_HARVESTING, ['treeHeight', 'volumePerWeekHarvestor']),
        ...excludeFields(MACHINES, ['harvesterMachine']),
        ...CHECKLIST,
        ...FINALIZE,
      ],
      Roadside: [
        ...excludeFields(GENERAL, ['siteReference']),

        ...MAP,
        ...pickFields(SITE_INFO, ['isSignalStrengthGood', 'access']),
        ...pickFields(HAZARDS, ['powerlines']),
        ...pickFields(SITE_VOLUME, ['totalVolume', 'totalTons']),
        ...pickFields(FINALIZE, ['siteNotesforHaulageOnly', 'siteNotesforForestManagerOnly']),
      ],
      'Sawmill Residue': [
        ...excludeFields(GENERAL, ['siteReference']),

        ...MAP,
        ...pickFields(SITE_INFO, ['access']),
        ...pickFields(FINALIZE, ['siteNotesforHaulageOnly']),
      ],
      'Delivered To Our Yard': [
        ...GENERAL,

        ...MAP,
        ...pickFields(SITE_INFO, ['isSignalStrengthGood', 'access']),
        ...pickFields(FINALIZE, ['siteNotesforHaulageOnly']),
      ],
    },
  },
  'Contract Work': {
    categories: ['Harvesting', 'Extracting', 'Haulage', 'Chipping'],
    fields: {
      Harvesting: [
        ...GENERAL,

        ...MAP,
        ...SUB_COMPARTMENTS,
        ...SITE_INFO,
        ...HAZARDS,
        ...excludeFields(SITE_VOLUME, ['percentOfForestCovered']),
        ...SITE_HARVESTING,
        ...MACHINES,
        ...CHECKLIST,
        ...FINALIZE,
      ],
      Extracting: [
        ...GENERAL,

        ...MAP,
        ...SUB_COMPARTMENTS,
        ...pickFields(SITE_INFO, ['isSignalStrengthGood', 'timberQuality', 'access']),
        ...HAZARDS,
        ...excludeFields(SITE_VOLUME, ['averageTree', 'treesPerHectare', 'percentOfForestCovered']),
        ...excludeFields(SITE_HARVESTING, ['treeHeight', 'volumePerWeekHarvestor']),
        ...excludeFields(MACHINES, ['harvesterMachine']),
        ...CHECKLIST,
        ...FINALIZE,
      ],
      Haulage: [
        ...pickFields(GENERAL, ['customer']),
        ...MAP,
        ...SUB_COMPARTMENTS,
        ...pickFields(HAZARDS, ['powerlines']),
        ...pickFields(SITE_VOLUME, ['totalVolume', 'totalTons']),
        ...excludeFields(FINALIZE, ['finaliseAssessment']),
      ],
      Chipping: [
        ...pickFields(GENERAL, ['customer']),
        ...MAP,
        ...SUB_COMPARTMENTS,
        ...pickFields(HAZARDS, ['powerlines']),
        ...pickFields(SITE_VOLUME, ['totalVolume', 'totalTons']),
        ...excludeFields(FINALIZE, ['finaliseAssessment']),
      ],
    },
  },
};

function AddNewSiteModal({ open, onClose }) {
  const {
    selectedType,
    selectedCategory,
    formData,
    fieldsBySection,
    isFormCreated,
    createdAssessmentId,
    setSelectedType,
    setSelectedCategory,
    handleFieldChange,
    createAssessment,
    // Map state
    mapData,
    mapState,
    openGoogleMapView,
    setOpenGoogleMapView,
    openRouteMapModal,
    setOpenRouteMapModal,
    routeDetails,
    // Map actions
    mapActions,
    saveMapCoordinates,
    updateMapCoordinates,
    deleteMapCoordinates,
    showSiteMap,
    openRouteView,
    getCurrentSiteRouteDetails,
    setSiteRef,
    siteRef,
    ownerName,
    setOwnerName,
    licenseNumber,
    setLicenseNumber,
    dropdownOptions,
    samples,
    // samplesData,
    subCompSampleData,
    handleAction,
    handleSubCompSampleData,
    ownerDrawerOpen,
    setOwnerDrawerOpen,
    saveNewOwner,
    handleMapClose,
  } = useNewSiteAssessment(assessmentConfig);

  // Filter sub-compartments from map data
  const subCompartments = Object.values(
    mapData.reduce((acc, item) => {
      if (item.mapType === 'polygon' && item.mapLabel?.toLowerCase().startsWith('sub')) {
        if (!acc[item.mapLabel]) {
          acc[item.mapLabel] = {
            ...item,
            points: [],
          };
        }
        acc[item.mapLabel].points.push(item);
      }
      return acc;
    }, {}),
  );

  // Filter pins for samples
  const pins = mapData.filter(item => item.mapType === 'pin');

  // Placeholder handlers for sub-compartment updates
  const handleOverviewUpdate = (subCompId, overviewInfo) => {
    console.log('Overview update for', subCompId, overviewInfo);
    // TODO: Implement API call to save overview info
  };

  const handleProductUpdate = (subCompId, products) => {
    console.log('Products update for', subCompId, products);
    // TODO: Implement API call to save products
  };

  const renderField = field => {
    const value = formData[field.name] || '';

    if (field.type === 'map') {
      return (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<MapIcon />} onClick={showSiteMap} fullWidth disabled={!createdAssessmentId}>
            {field.label}
          </Button>
          <Button
            variant="outlined"
            startIcon={<RouteIcon />}
            onClick={openRouteView}
            disabled={!createdAssessmentId}
            sx={{ minWidth: '120px' }}>
            Route
          </Button>
        </Box>
      );
    }

    if (field.type === 'radio') {
      return (
        <FormControl component="fieldset" fullWidth>
          <Typography variant="body2" sx={{ mb: 1 }}>
            {field.label}
          </Typography>
          <RadioGroup row value={value} onChange={e => handleFieldChange(field.name, e.target.value)}>
            {field.options?.map(option => (
              <FormControlLabel key={option} value={option} control={<Radio />} label={option} />
            ))}
          </RadioGroup>
        </FormControl>
      );
    }

    if (field.type === 'textarea') {
      return (
        <TextField
          fullWidth
          multiline
          rows={field.rows || 3}
          label={field.label}
          value={value}
          onChange={e => handleFieldChange(field.name, e.target.value)}
          disabled={field.readonly}
        />
      );
    }

    if (field.type === 'dropdown') {
      return (
        <FormControl fullWidth size="small">
          <InputLabel>{field.label}</InputLabel>
          <Select
            value={value}
            label={field.label}
            onChange={e => handleFieldChange(field.name, e.target.value)}
            disabled={field.readonly}>
            {field.options?.map(option => (
              <MenuItem key={option} value={option}>
                {option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    if (field.type === 'number') {
      return (
        <TextField
          fullWidth
          size="small"
          type="number"
          label={field.label}
          value={value}
          onChange={e => handleFieldChange(field.name, e.target.value)}
          disabled={field.readonly}
        />
      );
    }

    return (
      <TextField
        fullWidth
        size="small"
        label={field.label}
        value={value}
        onChange={e => handleFieldChange(field.name, e.target.value)}
        disabled={field.readonly}
      />
    );
  };

  function ChecklistTable({ fields, formData, onChange }) {
    return (
      <Paper variant="outlined">
        <Table sx={{ minWidth: 650 }} size="small">
          <TableHead>
            <TableRow>
              <TableCell width="60">✔</TableCell>
              <TableCell>Checklist Item</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {fields.map(field => (
              <TableRow key={field.name}>
                <TableCell>
                  <Checkbox checked={Boolean(formData[field.name])} onChange={e => onChange(field.name, e.target.checked)} />
                </TableCell>
                <TableCell>{field.label}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    );
  }

  const handleCreateAssessment = () => {
    createAssessment(siteRef);
  };

  const handleSubmit = () => {
    const payload = {
      assessmentType: selectedType,
      category: selectedCategory,
      siteRef,
      data: formData,
    };
    console.log('Submitting:', payload);
    onClose();
  };

  const handleClose = () => {
    setSiteRef('');
    setOwnerName('');
    setLicenseNumber('');
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        maxWidth="lg"
        fullWidth
        onClose={(event, reason) => {
          // Prevent closing on backdrop click or Escape key
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
          }
          handleClose();
        }}>
        <DialogTitle>
          New Site Assessment
          <IconButton aria-label="close" onClick={handleClose} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {!isFormCreated ? (
            // Initial Form: Type, Category, Site Ref
            <>
              {/* Step 1: Select Type */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Select Assessment Type *
                </Typography>
                <RadioGroup row value={selectedType} onChange={e => setSelectedType(e.target.value)}>
                  {Object.keys(assessmentConfig).map(type => (
                    <FormControlLabel key={type} value={type} control={<Radio />} label={type} />
                  ))}
                </RadioGroup>
              </Box>

              {/* Step 2: Select Category */}
              {selectedType && (
                <Box sx={{ mb: 3 }}>
                  <FormControl size="small" sx={{ minWidth: 300 }}>
                    <InputLabel>Category *</InputLabel>
                    <Select value={selectedCategory} label="Category *" onChange={e => setSelectedCategory(e.target.value)}>
                      {assessmentConfig[selectedType].categories.map(cat => (
                        <MenuItem key={cat} value={cat}>
                          {cat}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}

              {/* Step 3: Enter Site Ref */}
              {selectedCategory && (
                <Box sx={{ mb: 3 }}>
                  <TextField
                    size="small"
                    label="Site Reference *"
                    value={siteRef}
                    onChange={e => setSiteRef(e.target.value)}
                    sx={{ minWidth: 300 }}
                  />
                </Box>
              )}

              {/* Step 4: Owner Name */}
              {selectedCategory && (
                <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Autocomplete
                    size="small"
                    sx={{ width: 300 }}
                    options={dropdownOptions['OwnerNames'] || []}
                    value={ownerName || null}
                    onChange={(_, newValue) => setOwnerName(newValue || '')}
                    renderInput={params => <TextField {...params} sx={{ width: 300 }} label="Owner Name *" />}
                  />

                  <Button variant="contained" color="primary" onClick={() => setOwnerDrawerOpen(true)}>
                    Add Owner
                  </Button>
                </Box>
              )}

              {/* Step 5: License Number (depends on owner selection) */}
              {selectedCategory && ownerName && (
                <Box sx={{ mb: 3 }}>
                  <FormControl size="small" sx={{ minWidth: 300 }}>
                    <InputLabel>License Number</InputLabel>
                    <Select value={licenseNumber} label="License Number" onChange={e => setLicenseNumber(e.target.value)}>
                      {(dropdownOptions['LicenseNumbers'] || []).map(lic => (
                        <MenuItem key={lic} value={lic}>
                          {lic}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              )}
              <AddOwnerDrawer open={ownerDrawerOpen} onClose={() => setOwnerDrawerOpen(false)} onSave={saveNewOwner} />
            </>
          ) : (
            // Full Form Fields (after creation)
            <Box>
              {/* Show selected type, category, and site ref */}
              <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={3}>
                    <Typography variant="caption" color="text.secondary">
                      Assessment Type
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {selectedType}
                    </Typography>
                  </Grid>
                  <Grid item xs={3}>
                    <Typography variant="caption" color="text.secondary">
                      Category
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {selectedCategory}
                    </Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography variant="caption" color="text.secondary">
                      Site Reference
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {siteRef}
                    </Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography variant="caption" color="text.secondary">
                      Owner
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {ownerName}
                    </Typography>
                  </Grid>
                  <Grid item xs={2}>
                    <Typography variant="caption" color="text.secondary">
                      License
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {licenseNumber || '—'}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              {/* Form Fields grouped by section */}
              {Object.entries(fieldsBySection).map(([section, fields]) => {
                if (section === 'Checklist') {
                  return (
                    <Box key={section} sx={{ mb: 3, maxWidth: '70%' }}>
                      <Typography variant="body2" color="primary" sx={{ mb: 1.5, fontWeight: 500 }}>
                        Checklist
                      </Typography>

                      <ChecklistTable fields={fields} formData={formData} onChange={handleFieldChange} />
                    </Box>
                  );
                }

                if (section === 'Sub Compartment' && subCompartments.length > 0) {
                  return (
                    <SubCompartmentSection
                      createdAssessmentId={createdAssessmentId}
                      subCompartments={subCompartments}
                      pins={pins}
                      samplePoints={samples}
                      samplesData={subCompSampleData}
                      dropDownOptions={dropdownOptions['Species']}
                      handleAction={handleAction}
                      handleSampleData={handleSubCompSampleData}
                      handleOverviewUpdate={handleOverviewUpdate}
                      handleProductUpdate={handleProductUpdate}
                      selectedCategory={selectedCategory}
                    />
                  );
                } else {
                  return (
                    <Box key={section} sx={{ mb: 3 }}>
                      <Typography variant="body2" color="primary" sx={{ mb: 1.5, fontWeight: 500 }}>
                        {section}
                      </Typography>
                      <Grid container spacing={2}>
                        {fields.map(field => (
                          <Grid item xs={12} sm={6} md={4} key={field.name}>
                            {renderField(field)}
                          </Grid>
                        ))}
                      </Grid>
                    </Box>
                  );
                }
              })}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          <AutoSaveIndicator isFormCreated={isFormCreated} />
          <Button onClick={handleClose}>Cancel</Button>
          {!isFormCreated ? (
            <Button
              variant="contained"
              onClick={handleCreateAssessment}
              disabled={!selectedType || !selectedCategory || !siteRef.trim() || !ownerName.trim()}>
              Create Assessment
            </Button>
          ) : (
            <Button variant="contained" onClick={handleSubmit}>
              Save
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Map Editor Dialog - Only render when form is created */}
      {isFormCreated && createdAssessmentId && (
        <Dialog open={openGoogleMapView} maxWidth="xl" onClose={handleMapClose} disableAutoFocus disableEnforceFocus>
          <DialogTitle>Geo Locations</DialogTitle>
          <DialogContent style={{ width: '90vw', height: '80vh' }}>
            <MapEditor
              mapState={mapState}
              mapActions={mapActions}
              mapData={mapData}
              saveMapCoordinates={saveMapCoordinates}
              updateMapCoordinates={updateMapCoordinates}
              deleteMapCoordinates={deleteMapCoordinates}
              boundaryAlreadyExists={false}
              mapHeight="75vh"
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleMapClose} variant="contained" color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Route View Dialog - Only render when form is created */}
      {isFormCreated && createdAssessmentId && (
        <MapRouteView
          open={openRouteMapModal}
          setOpen={setOpenRouteMapModal}
          mapCordinateDetails={mapData}
          routeDetails={routeDetails}
          getRouteDetails={getCurrentSiteRouteDetails}
          siteMainData={createdAssessmentId}
        />
      )}
    </>
  );
}

export default AddNewSiteModal;
